import { useNavigate } from 'react-router-dom';
import { Card } from '../atoms/Card';
import { Badge, type BadgeTone } from '../atoms/Badge';
import { Button } from '../atoms/Button';
import { EmptyState } from '../atoms/EmptyState';
import { Icon } from '../atoms/Icon/Icon';
import { Inline } from '../atoms/Inline';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack';
import { Heading, Text } from '../atoms/Typography';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { Section } from '../templates/Section';
import { useTranslation } from '../../i18n/useTranslation';
import { useLatestZeovitRecommendation } from '../../hooks/useZeovitRecommendations';
import type { ZeovitRecommendationStatus } from '../../services/zeovitRecommendationService';
import './ZeovitRecommendationCardSection.css';

export interface ZeovitRecommendationCardSectionProps {
  aquariumId: string | undefined;
}

/** Status → Badge tone + decorative icon. The Badge text always carries the meaning. */
const STATUS_PRESENTATION: Record<ZeovitRecommendationStatus, { tone: BadgeTone; icon: string }> = {
  pending: { tone: 'info', icon: 'clock' },
  adopted: { tone: 'success', icon: 'check' },
  superseded: { tone: 'neutral', icon: 'history' },
  capped: { tone: 'warning', icon: 'alert-triangle' },
};

function formatDate(iso: string | null | undefined, language: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

/**
 * ZeoVit section of the Reef Intelligence area on the aquarium page (#1444):
 * shows the latest dosing recommendation's status, or a CTA to get the first one.
 */
export function ZeovitRecommendationCardSection({ aquariumId }: ZeovitRecommendationCardSectionProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError, isFetching, refetch } = useLatestZeovitRecommendation(aquariumId);

  if (!aquariumId) return null;

  const base = `/aquariums/${encodeURIComponent(aquariumId)}/zeovit-recommendations`;

  let body;
  if (isLoading) {
    body = (
      <Stack gap="3">
        <LiveRegion busy>
          <Text color="muted">{t('zeovit.card.loading')}</Text>
        </LiveRegion>
      </Stack>
    );
  } else if (isError) {
    body = (
      <LoadErrorState
        title={t('zeovit.error.loadFailed')}
        titleLevel={3}
        retryLabel={t('zeovit.error.retry')}
        retrying={isFetching}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  } else if (!data) {
    body = (
      <EmptyState
        icon={<Icon name="flask" size="lg" />}
        headingLevel={3}
        headline={t('zeovit.empty.title')}
        description={t('zeovit.empty.description')}
        actionLabel={t('zeovit.empty.cta')}
        onAction={() => navigate(`${base}/new`)}
      />
    );
  } else {
    const status = STATUS_PRESENTATION[data.status] ?? STATUS_PRESENTATION.pending;
    const updatedAt = data.adoptedAt ?? data.createdAt;
    const date = formatDate(updatedAt, i18n.language);
    const detailPath = `${base}/${encodeURIComponent(data.id)}`;
    // A capped loop gets no further AI action (spec: manual review instead).
    const canRunCheck = data.status === 'pending' || data.status === 'adopted';

    body = (
      <Stack gap="3">
        <Inline gap="2" justify="between" align="start" wrap>
          <Stack gap="1" className="zeovit-card-section__intro">
            <Heading level={3}>{t('zeovit.card.heading')}</Heading>
            <Text color="muted">{t('zeovit.card.summary')}</Text>
          </Stack>
          <Badge tone={status.tone}>
            <Icon name={status.icon} size="sm" />
            {t(`zeovit.status.${data.status}`)}
          </Badge>
        </Inline>
        {date ? (
          <Text color="muted">
            <time dateTime={updatedAt}>{t('zeovit.card.lastUpdated', { date })}</time>
          </Text>
        ) : null}
        <Inline gap="2" wrap className="zeovit-card-section__actions">
          <Button variant="primary" onClick={() => navigate(detailPath)}>
            {t('zeovit.card.viewRecommendation')}
          </Button>
          {canRunCheck ? (
            <Button variant="secondary" onClick={() => navigate(`${detailPath}/check`)}>
              {t('zeovit.card.runCheck')}
            </Button>
          ) : null}
        </Inline>
      </Stack>
    );
  }

  return (
    <Section title={t('zeovit.card.title')} data-testid="zeovit-card-section">
      <Card>{body}</Card>
    </Section>
  );
}

export default ZeovitRecommendationCardSection;
